import { useEffect, useRef } from 'react'

import { useAuth } from '@/lib/auth'
import { subscribeToBrowserEvent } from '@/lib/signalr/connection-manager'

import { browserEventKey, type BrowserEvent } from './browser-event'

export function useOnEvent<TArguments extends unknown[]>(
  event: BrowserEvent<TArguments>,
  handler: (...arguments_: NoInfer<TArguments>) => void | Promise<void>,
): void {
  useBrowserEventSubscription(event, handler)
}

export function useRefreshOnEvent<TArguments extends unknown[]>(
  event: BrowserEvent<TArguments>,
  refresh: () => void | Promise<void>,
): void {
  useBrowserEventSubscription(event, () => refresh(), () => void refresh())
}

function useBrowserEventSubscription<TArguments extends unknown[]>(
  event: BrowserEvent<TArguments>,
  handler: (...arguments_: NoInfer<TArguments>) => void | Promise<void>,
  onResubscribed?: () => void,
): void {
  const { isAuthenticated } = useAuth()
  const eventRef = useRef(event)
  const handlerRef = useRef(handler)
  const resubscribedRef = useRef(onResubscribed)
  useEffect(() => {
    eventRef.current = event
    handlerRef.current = handler
    resubscribedRef.current = onResubscribed
  })

  const key = browserEventKey(event)
  useEffect(() => {
    if (!isAuthenticated) return
    return subscribeToBrowserEvent(
      eventRef.current,
      (...arguments_) => handlerRef.current(...arguments_),
      () => resubscribedRef.current?.(),
    )
  }, [isAuthenticated, key])
}
